import { message } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import shallow from 'zustand/shallow';

import { segment } from '../services';
import { useTemplatesStore } from '../stores';
import { Campaign, ICampaignFormHook, ICampaignFormReturnValue, Template } from '../typings';
import { IMAGE_RATIO, config, copyTextToClipboard, getRendererUrl, parseCustomText } from '../utils';

interface Props {
  selectedTemplate: Template;
  onSelectedTemplateChange: (value: Template) => void;
}

export const useCampaignForm = ({ selectedTemplate, onSelectedTemplateChange }: Props): ICampaignFormHook => {
  const [form] = useForm();
  const [previewForm] = useForm();
  const [previewUrl, setPreviewUrl] = React.useState<string>();
  const [codeText, setCodeText] = React.useState<string>();

  const { t } = useTranslation();

  const {
    fetchTemplates,
    templates: { listTemplates },
  } = useTemplatesStore(
    ({ fetchTemplates, templates }) => ({
      fetchTemplates,
      templates,
    }),
    shallow
  );

  const [{ loading: fetchTemplatesLoading, error: fetchTemplatesError }, fetchTemplatesAction] = useAsyncFn(
    fetchTemplates
  );

  const [{ loading: onCopyLoading, error: onCopyError }, handleOnCopy] = useAsyncFn(
    async (campaign: NonNullable<Campaign>) => {
      segment.trackCampaignCodeCopyAnalytics(campaign);
      const copied = await copyTextToClipboard(codeText);
      if (copied) message.success(t('COPY_SUCCESS_MESSAGE'));
    },
    [codeText]
  );

  const [{ loading: updatePreviewLoading, error: updatePreviewError }, updatePreview] = useAsyncFn<
    ICampaignFormHook['updatePreview']
  >(async () => {
    const customFields = parseCustomText(form.getFieldValue('customText') || {});
    let sample = (previewForm.getFieldValue('sample') || '').trim();

    if (selectedTemplate) {
      segment.trackPreviewAnalytics(selectedTemplate);
      const previewUrl = getRendererUrl({ text: sample, templateId: selectedTemplate.id, customText: customFields });
      const response = fetch ? await fetch(`${previewUrl}&$`) : null;
      if (!response || response.ok) {
        setPreviewUrl(previewUrl);
      } else {
        message.error(t('PREVIEW_ERROR_MESSAGE'));
      }
    }
  }, [selectedTemplate]);

  const createCodeText = async (campaign: NonNullable<Campaign>) => {
    const loaderImageUrl = config['LOADER_IMG_URL'];

    setCodeText(
      `<img src="${getRendererUrl({
        text: 'REPLACE_WITH_FIRSTNAME',
        campaignId: campaign.id,
      })}" width="${campaign.width}" height="${Math.round(
        campaign.width * IMAGE_RATIO
      )}" style="background: url(${loaderImageUrl}) no-repeat center center" />`
    );
  };

  const handleSelectScroll = async (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const target = event.currentTarget;
    if (
      Math.ceil(target.scrollTop + target.offsetHeight) === target.scrollHeight &&
      listTemplates &&
      listTemplates.nextToken
    )
      await fetchTemplatesAction();
  };

  const onValuesChange = (changedValues: ICampaignFormReturnValue) => {
    if (changedValues && changedValues.templateId && listTemplates && listTemplates.items) {
      const selectedTemplate = listTemplates.items.find((template) => template?.id === changedValues.templateId);
      onSelectedTemplateChange(selectedTemplate || null);
    }
  };

  const options = React.useMemo(() => {
    if (listTemplates && listTemplates.items) {
      const options = listTemplates.items
        .filter((template) => template !== null)
        .map((template) => ({ value: template?.id as string, label: template?.name }));

      if (selectedTemplate)
        return options.some((option) => option.value === selectedTemplate.id)
          ? options
          : [
              {
                value: selectedTemplate.id,
                label: selectedTemplate.name,
              },
              ...options,
            ];
      else return options;
    }
    return [];
  }, [listTemplates, selectedTemplate]);

  React.useEffect(() => {
    (!listTemplates || (listTemplates && !listTemplates.items)) && fetchTemplatesAction();
  }, [fetchTemplatesAction, listTemplates]);

  React.useEffect(() => {
    if (fetchTemplatesError) message.error(fetchTemplatesError.message);
    if (onCopyError) message.error(onCopyError.message);
    if (updatePreviewError) message.error(updatePreviewError.message);
  }, [fetchTemplatesError, onCopyError, updatePreviewError]);

  return {
    form,
    previewForm,
    listTemplates: listTemplates?.items,
    fetchTemplatesLoading,
    onCopyLoading,
    updatePreviewLoading,
    options,
    previewUrl,
    codeText,
    handleSelectScroll,
    onValuesChange,
    handleOnCopy,
    updatePreview,
    createCodeText,
  };
};
